// src/pages/MainPanel/components/RegistryTagTable.jsx
//
// Shared presentational component for a registry repository's tag list. Used by:
// - Workflow/components/RegistryPanel.jsx (read-only, pre-loaded)
// - CreateExperiment/ImageSelector.jsx (selectable, lazily-loaded)

import PropTypes from "prop-types";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// ---- helpers ----

function formatSize(bytes) {
  if (bytes == null) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function shortDigest(digest) {
  if (!digest) return "—";
  const colon = digest.indexOf(":");
  const hex = colon >= 0 ? digest.slice(colon + 1) : digest;
  return `${digest.slice(0, colon + 1)}${hex.slice(0, 7)}`;
}

const REGISTRY_PREFIX = "registry.efabric.cs.wisc.edu/efabric/";
function stripPrefix(ref) {
  return ref?.startsWith(REGISTRY_PREFIX)
    ? ref.slice(REGISTRY_PREFIX.length)
    : (ref ?? "");
}

// ---- component ----

export default function RegistryTagTable({
  location,
  tags,
  selectedRef,
  onTagSelect,
  disabled,
}) {
  if (!tags || tags.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
        No tags published
      </Typography>
    );
  }

  const interactive = !!onTagSelect;

  return (
    <Table size="small" sx={{ tableLayout: "fixed" }}>
      <TableHead>
        <TableRow
          sx={{
            "& th": {
              fontWeight: 600,
              color: "text.secondary",
              fontSize: "0.72rem",
              py: 0.5,
            },
          }}
        >
          <TableCell sx={{ width: "28%" }}>Tag</TableCell>
          <TableCell sx={{ width: "20%" }}>Size</TableCell>
          <TableCell sx={{ width: "30%" }}>Digest</TableCell>
          <TableCell sx={{ width: "22%" }}>Published</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tags.map((tag) => {
          const fullRef = `${location}:${tag.name}`;
          const shortRef = stripPrefix(fullRef);
          const isSelected =
            !!selectedRef &&
            (selectedRef === shortRef || selectedRef === fullRef);
          return (
            <TableRow
              key={tag.name}
              hover
              selected={isSelected}
              onClick={() => interactive && !disabled && onTagSelect(shortRef)}
              sx={{
                "& td": {
                  py: 0.5,
                  fontSize: "0.8rem",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
                cursor: interactive && !disabled ? "pointer" : "default",
              }}
            >
              {/* Tag chip + copy */}
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Chip
                    label={tag.name}
                    size="small"
                    variant={isSelected ? "filled" : "outlined"}
                    color={isSelected ? "primary" : "default"}
                    sx={{
                      fontSize: "0.72rem",
                      height: 20,
                      fontFamily: "monospace",
                    }}
                  />
                  <Tooltip title={`Copy: ${shortRef}`} placement="top">
                    <IconButton
                      size="small"
                      sx={{ p: 0.25 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard?.writeText(shortRef);
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>

              {/* Size */}
              <TableCell sx={{ fontFamily: "monospace" }}>
                {formatSize(tag.size)}
              </TableCell>

              {/* Digest (truncated, full on hover) */}
              <TableCell>
                <Tooltip title={tag.digest || "—"} placement="top">
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      cursor: "default",
                      color: "text.secondary",
                    }}
                  >
                    {shortDigest(tag.digest)}
                  </Typography>
                </Tooltip>
              </TableCell>

              {/* Published date */}
              <TableCell sx={{ color: "text.secondary" }}>
                {formatDate(tag.createdAt)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

RegistryTagTable.propTypes = {
  location: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number,
      digest: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  ).isRequired,
  selectedRef: PropTypes.string,
  onTagSelect: PropTypes.func,
  disabled: PropTypes.bool,
};
